import React, { useState } from 'react';
import { Card, Button } from '../UI';
import { ShoppingCart, Trash2, Check, LogIn, X, Plus, Circle, Package } from 'lucide-react';
import { useGuestList } from '../../hooks/useGuestList';

const GuestGroceryList = () => {
  const { guestList, removeItem, updateItem, clearList, addItem } = useGuestList();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  if (!guestList) {
    return (
      <div className="text-center py-xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md"></div>
        <p className="text-callout text-text-secondary">Loading...</p>
      </div>
    );
  }

  const items = guestList.items || [];
  const uncheckedItems = items.filter(item => !item.isChecked);
  const checkedItems = items.filter(item => item.isChecked);
  const progress = items.length > 0 ? (checkedItems.length / items.length) * 100 : 0;

  const handleToggleCheck = (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      updateItem(itemId, { isChecked: !item.isChecked });
    }
  };

  const handleClearChecked = () => {
    const unchecked = items.filter(item => !item.isChecked);
    clearList();
    unchecked.forEach(item => {
      const { id, addedAt, ...itemData } = item;
      addItem(itemData);
    });
    setShowClearConfirm(false);
  };

  const handleAddItem = () => {
    if (!newItemName.trim()) return;

    const newItem = {
      name: newItemName,
      quantity: 1,
      category: 'other',
      isChecked: false,
    };

    addItem(newItem);
    setNewItemName('');
  };

  return (
    <div className="space-y-xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-lg">
        <div>
          <h1 className="text-display font-rounded text-primary mb-sm">Shopping List</h1>
          <p className="text-body text-text-secondary">Guest mode - saved locally</p>
        </div>
      </div>

      {/* Guest Notice - More Padding */}
      <Card className="bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20 p-lg">
        <div className="flex items-center justify-between gap-lg">
          <div className="flex items-center gap-lg flex-1">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <ShoppingCart size={22} className="text-accent" />
            </div>
            <div>
              <p className="text-callout font-semibold text-text-primary mb-xs">
                Browsing as Guest
              </p>
              <p className="text-caption text-text-secondary">
                Sign in to sync your list across devices and create multiple lists
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            icon={<LogIn size={18} />}
            size="small"
            onClick={() => window.location.href = '/login'}
          >
            Sign In
          </Button>
        </div>
      </Card>

      {/* Progress Card - More Padding */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/10 p-lg">
        <div className="space-y-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-title-3 text-primary mb-sm">Progress</h3>
              <p className="text-caption text-text-secondary">
                {checkedItems.length} of {items.length} items checked
              </p>
            </div>
            <div className="text-right">
              <div className="text-title-1 font-bold text-primary">
                {Math.round(progress)}%
              </div>
            </div>
          </div>
          
          <div className="w-full h-3 bg-surface/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {checkedItems.length > 0 && (
            <div className="pt-md border-t border-text-tertiary/10">
              {!showClearConfirm ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="text-callout text-text-secondary hover:text-error transition-colors flex items-center gap-sm"
                >
                  <Trash2 size={16} />
                  Clear {checkedItems.length} checked item{checkedItems.length !== 1 ? 's' : ''}
                </button>
              ) : (
                <div className="flex items-center gap-md">
                  <p className="text-callout text-text-secondary flex-1">
                    Remove checked items?
                  </p>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => setShowClearConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleClearChecked}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Add Item - More Padding */}
      <Card className="border-primary/20 p-lg">
        <div className="flex gap-md">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Add item to list..."
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
              className="w-full px-lg py-md bg-surface/50 border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body placeholder:text-text-tertiary"
            />
          </div>
          <Button
            variant="primary"
            icon={<Plus size={18} />}
            onClick={handleAddItem}
            disabled={!newItemName.trim()}
          >
            Add
          </Button>
        </div>
      </Card>

      {/* Items List - Product Cards with Images */}
      <div className="space-y-md">
        {uncheckedItems.length > 0 && (
          <div className="space-y-sm">
            <h3 className="text-callout font-semibold text-text-secondary px-md mb-md">
              To Buy ({uncheckedItems.length})
            </h3>
            {uncheckedItems.map(item => (
              <Card 
                key={item.id}
                className="hover:shadow-md transition-all border-text-tertiary/10 hover:border-primary/20 p-lg"
              >
                <div className="flex items-center gap-lg">
                  {/* Checkbox */}
                  <button
                    onClick={() => handleToggleCheck(item.id)}
                    className="flex-shrink-0 w-7 h-7 rounded-full border-2 border-text-tertiary/30 hover:border-primary transition-colors flex items-center justify-center"
                  >
                    <Circle size={14} className="text-text-tertiary" />
                  </button>

                  {/* Product Image */}
                  {item.productImageURL ? (
                    <div className="w-16 h-16 rounded-medium bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img 
                        src={item.productImageURL} 
                        alt={item.name}
                        className="w-full h-full object-contain p-xs"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-medium bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center flex-shrink-0">
                      <Package size={24} className="text-text-tertiary" />
                    </div>
                  )}

                  {/* Item Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-callout font-semibold text-text-primary truncate">
                      {item.name}
                    </h4>
                    <div className="flex items-center gap-md mt-xs">
                      {item.quantity > 1 && (
                        <p className="text-caption text-text-tertiary">
                          Qty: {item.quantity}
                        </p>
                      )}
                      {item.category && item.category !== 'other' && (
                        <span className="px-sm py-xs bg-primary/10 text-primary text-caption rounded-small">
                          {item.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="flex-shrink-0 w-9 h-9 rounded-full hover:bg-error/10 flex items-center justify-center text-text-tertiary hover:text-error transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {checkedItems.length > 0 && (
          <div className="space-y-sm pt-xl">
            <h3 className="text-callout font-semibold text-text-secondary px-md mb-md">
              Checked ({checkedItems.length})
            </h3>
            {checkedItems.map(item => (
              <Card 
                key={item.id}
                className="bg-surface/50 border-text-tertiary/5 opacity-60 p-lg"
              >
                <div className="flex items-center gap-lg">
                  {/* Checked Checkbox */}
                  <button
                    onClick={() => handleToggleCheck(item.id)}
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-success flex items-center justify-center"
                  >
                    <Check size={16} className="text-white" />
                  </button>

                  {/* Product Image - Grayscale when checked */}
                  {item.productImageURL ? (
                    <div className="w-16 h-16 rounded-medium bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden grayscale">
                      <img 
                        src={item.productImageURL} 
                        alt={item.name}
                        className="w-full h-full object-contain p-xs"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-medium bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center flex-shrink-0">
                      <Package size={24} className="text-text-tertiary" />
                    </div>
                  )}

                  {/* Item Info - Strikethrough */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-callout text-text-secondary line-through truncate">
                      {item.name}
                    </h4>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="flex-shrink-0 w-9 h-9 rounded-full hover:bg-error/10 flex items-center justify-center text-text-tertiary hover:text-error transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {items.length === 0 && (
          <Card className="text-center py-xxxl bg-gradient-to-br from-surface to-primary/5 border-text-tertiary/10 p-xl">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-lg">
              <ShoppingCart size={36} className="text-primary" />
            </div>
            <h3 className="text-title-3 text-primary mb-md">Your list is empty</h3>
            <p className="text-body text-text-secondary">
              Add items to start shopping
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GuestGroceryList;
